import {
  Alert,
  AlertDescription,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Checkbox,
  CheckboxGroup,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AdminLayout,
  Button,
  Card,
  DataTable,
  SearchInput,
  SelectInput,
  StatsCard,
} from '@grcs/components';
import { TableColumn } from '@grcs/components/types';
import { useData } from '@grcs/context/DataContext';
import {
  useClassOperations,
  useStudentOperations,
} from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import {
  ArticleProfile,
  ClassProfile,
  StudentProfile,
} from '@grcs/types/DataTypes';
import { TABLE_NAMES } from '@grcs/utils/database/types';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FiBookOpen,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiEye,
  FiFilter,
  FiGrid,
  FiList,
  FiPlus,
  FiSend,
  FiSettings,
  FiUsers,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

interface PublishingStats {
  totalContent: number;
  totalStudents: number;
}

interface ContentWithMetadata extends ArticleProfile {
  difficulty: 'easy' | 'medium' | 'hard';
  estimatedTime: number;
}

type ViewMode = 'grid' | 'table';
type FilterBy = 'all' | 'grade4' | 'grade5' | 'grade6' | 'grade7' | 'grade8';
type SortBy = 'title' | 'grade' | 'chapter' | 'difficulty' | 'recent';

export const ClassPublishHomeworkEnhanced: React.FC = () => {
  const { classId, year } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [{ user_token, full_name, email }] = useLocalStorage('user', {});
  const {
    isOpen: isPublishModalOpen,
    onOpen: onPublishModalOpen,
    onClose: onPublishModalClose,
  } = useDisclosure();

  // Data fetching hooks
  const classOps = useClassOperations();
  const studentOps = useStudentOperations();

  // Replace problematic useTableSelectedData with manual fetching
  const [libraryTableData, setLibraryTableData] = useState<ArticleProfile[]>(
    [],
  );
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { supabase } = useData();

  // Fetch library data
  useEffect(() => {
    const fetchLibraryData = async () => {
      if (!supabase) {
        console.log('⚠️ No supabase client available');
        return;
      }

      console.log('🚀 Starting library data fetch...');
      const startTime = Date.now();

      try {
        setLibraryLoading(true);

        // Fetch full dataset with count so client-side filters/pagination stay in sync
        const dataResult = await supabase
          .from('library')
          .select('id, title, author, grade, chapter, created_at', {
            count: 'exact',
          })
          .eq('deleted', false)
          .order('id', { ascending: true });

        // Set both data and count from single query
        if (dataResult.count !== null) {
          setTotalCount(dataResult.count);
        }

        if (dataResult.error) {
          console.error('Paginated query error:', dataResult.error);

          // Simple fallback: try without deleted filter
          const fallbackResult = await supabase
            .from('library')
            .select('id, title, author, grade, chapter, created_at', {
              count: 'exact',
            })
            .order('id', { ascending: true });

          if (fallbackResult.data) {
            setLibraryTableData(fallbackResult.data as ArticleProfile[]);
            if (fallbackResult.count !== null) {
              setTotalCount(fallbackResult.count);
            }
          }
        } else if (dataResult.data) {
          setLibraryTableData(dataResult.data as ArticleProfile[]);
        }
      } catch (error) {
        console.error('Error fetching library data:', error);
      } finally {
        const endTime = Date.now();
        console.log(
          `⏱️ Library data fetch completed in ${endTime - startTime}ms`,
        );
        setLibraryLoading(false);
      }
    };

    fetchLibraryData();
  }, [supabase]);

  // State
  const [contents, setContents] = useState<ContentWithMetadata[]>([]);
  const [selectedContent, setSelectedContent] =
    useState<ContentWithMetadata | null>(null);
  const [selectedHomework, setSelectedHomework] = useState<string>();
  const [classInfo, setClassInfo] = useState<ClassProfile | null>(null);
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(
    () => new Set(),
  );
  const [unavaiableStudents, setUnavailableStudents] = useState<Set<string>>(
    () => new Set(),
  );
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);

  // UI state with localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('publishHomework_viewMode');
    return (saved as ViewMode) ?? 'grid';
  });
  const [filterBy, setFilterBy] = useState<FilterBy>(() => {
    const saved = localStorage.getItem('publishHomework_filterBy');
    return (saved as FilterBy) || 'all';
  });
  const [sortBy, setSortBy] = useState<SortBy>(() => {
    const saved = localStorage.getItem('publishHomework_sortBy');
    return (saved as SortBy) || 'grade';
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    const saved = localStorage.getItem('publishHomework_searchQuery');
    return saved || '';
  });
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('publishHomework_pageSize');
    return saved ? parseInt(saved) : 10;
  });

  // Cleanup corrupted localStorage values on mount (one-time fix)
  useEffect(() => {
    const cleanupCorruptedValues = () => {
      const keys = [
        'publishHomework_viewMode',
        'publishHomework_filterBy',
        'publishHomework_sortBy',
        'publishHomework_searchQuery',
        'publishHomework_pageSize',
      ];

      keys.forEach((key) => {
        const value = localStorage.getItem(key);
        // Check if value is double-stringified (starts with quote)
        if (value && (value.startsWith('"') || value.startsWith('"\\"'))) {
          console.log(`Cleaning corrupted localStorage key: ${key}`);
          localStorage.removeItem(key);
        }
      });
    };

    cleanupCorruptedValues();
  }, []); // Run only once on mount

  // Save to localStorage whenever these values change
  useEffect(() => {
    localStorage.setItem('publishHomework_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('publishHomework_filterBy', filterBy);
  }, [filterBy]);

  useEffect(() => {
    localStorage.setItem('publishHomework_sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('publishHomework_searchQuery', searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    localStorage.setItem('publishHomework_pageSize', String(pageSize));
  }, [pageSize]);

  // Fetch class and student data
  useEffect(() => {
    const fetchClassData = async () => {
      if (!classId || !year) return;

      try {
        setLoading(true);

        // Get class information
        const classResult = await classOps.getClassesBySemester(year);
        const currentClass = classResult.data?.find(
          (c) => c.class_token === classId,
        );
        if (currentClass) {
          setClassInfo(currentClass);
        }

        // Get students in class
        const studentsResult = await studentOps.getStudentsByClassId(classId!);
        const students = studentsResult.data ?? [];
        setStudents(students);
      } catch (error) {
        console.error('Error fetching class data:', error);
        toast({
          title: 'Error loading class data',
          description: 'Failed to load class information. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchClassData();
  }, [classId, year, classOps, studentOps, toast]);

  // Process library content with metadata (optimized)
  useEffect(() => {
    console.log('🔄 Processing effect triggered:', {
      libraryTableData: libraryTableData?.length || 0,
      hasLibraryData: !!libraryTableData,
    });

    if (libraryTableData && libraryTableData.length > 0) {
      console.log('📝 Starting to process library data...');

      // Use requestIdleCallback or setTimeout for non-blocking processing
      const processData = () => {
        const processedContent: ContentWithMetadata[] = libraryTableData.map(
          (article: ArticleProfile) => {
            // Simplified processing for better performance
            const gradeNum = parseInt(article.grade?.toString() || '1') || 1;
            const difficulty: 'easy' | 'medium' | 'hard' =
              gradeNum <= 2 ? 'easy' : gradeNum <= 4 ? 'medium' : 'hard';

            return {
              ...article,
              difficulty,
              estimatedTime: Math.max(10, gradeNum * 3), // Simplified calculation
            };
          },
        );

        setContents(processedContent);
      };

      // Use setTimeout to avoid blocking the UI
      const timeoutId = setTimeout(processData, 0);
      return () => clearTimeout(timeoutId);
    } else {
      setContents([]);
    }
  }, [libraryTableData]);

  // Calculate statistics
  const stats: PublishingStats = useMemo(() => {
    return {
      totalContent: totalCount, // Use database count instead of current page content
      totalStudents: students.length,
    };
  }, [students.length, totalCount]);

  // Filter and sort content (optimized)
  const filteredContent = useMemo(() => {
    console.log('🔍 Filtering content...', {
      contentCount: contents.length,
      searchQuery,
      filterBy,
      sortBy,
    });

    let filtered = contents;

    // Search filter (simplified)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (content) =>
          content.title?.toLowerCase().includes(query) ||
          content.author?.toLowerCase().includes(query) ||
          content.grade?.toString().includes(query),
      );
    }

    // Grade filter (simplified)
    if (filterBy !== 'all') {
      const targetGrade = filterBy.replace('grade', '');
      filtered = filtered.filter(
        (content) => content.grade?.toString().startsWith(targetGrade),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'chapter': {
          const toNum = (v: string) => {
            return Number(String(v ?? '').trim());
          };
          return toNum(a.chapter?.toString()) - toNum(b.chapter?.toString());
        }
        case 'grade': {
          // Sort by grade first, then by chapter
          const gradeCompare = (a.grade?.toString() || '').localeCompare(
            b.grade?.toString() || '',
          );
          if (gradeCompare !== 0) return gradeCompare;

          // If grades are equal, sort by chapter
          const toNum = (v: string) => {
            return Number(String(v ?? '').trim());
          };
          return toNum(a.chapter?.toString()) - toNum(b.chapter?.toString());
        }
        case 'difficulty':
          const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
          return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
        case 'recent':
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case 'title':
        default:
          return (a.title || '').localeCompare(b.title || '');
      }
    });

    console.log('✅ Content filtering complete', {
      filteredCount: filtered.length,
    });
    return filtered;
  }, [contents, searchQuery, filterBy, sortBy]);

  const filteredTotalCount = filteredContent.length;

  const paginatedContent = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredContent.slice(startIndex, startIndex + pageSize);
  }, [filteredContent, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredTotalCount / pageSize));
  }, [filteredTotalCount, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterBy, sortBy, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Send notification emails to students
  const sendNotificationEmails = async (
    contentInfo: ArticleProfile,
    homeworkNeededStudentId: StudentProfile[],
  ) => {
    const studentEmailsInfo = homeworkNeededStudentId?.map((student: any) => ({
      email_address: {
        address: student.email,
        name: student.first_name + ' ' + student.last_name,
      },
    }));

    const publisherEmailInfo = {
      email_address: {
        address: email,
        name: full_name,
      },
    };

    const articleInfo = {
      title: contentInfo.title,
      grade: contentInfo.grade,
    };

    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        className: classInfo?.class_name,
        studentEmailsInfo,
        publisherEmailInfo,
        articleInfo,
      },
    });

    if (error) {
      console.error('Failed to send email notifications:', error);
    } else {
      console.log('Email notifications sent successfully:', data);
    }
  };

  // Execute publish - REAL IMPLEMENTATION
  const executePublish = useCallback(async () => {
    if (!supabase || !classInfo || !selectedContent) {
      console.error('Missing required data for publishing');
      return;
    }
    // Show loading toast
    const loadingToastId = toast({
      title: 'Publishing homework...',
      description: `Publishing "${selectedContent.title}" to students.`,
      status: 'loading',
      duration: null,
      isClosable: false,
    });
    try {
      setPublishing(true);
      console.log('🚀 Starting homework publish process...', {
        contentId: selectedContent.id,
        targetStudents: selectedStudents,
        classId: classId,
      });

      let homeworkToken = selectedHomework;
      if (!homeworkToken) {
        // Create new homework record (assignment record linking article to class)
        const homeworkData = {
          title: selectedContent.title,
          article_token: selectedContent.id,
          class_token: classId,
          publisher_token: user_token || 'system',
          publisher_name: full_name || 'System',
          grade: selectedContent.grade,
          chapter: selectedContent.chapter || selectedContent.title,
          created_at: new Date().toISOString(),
          deleted: false,
        };
        console.log('📝 Creating new homework record:', homeworkData);

        const { data: homeworkResult, error: homeworkError } = await supabase
          .from(TABLE_NAMES.HOMEWORK)
          .insert(homeworkData)
          .select('homework_token')
          .single();

        if (homeworkError) {
          console.error('Error creating homework:', homeworkError);
          throw homeworkError;
        }

        homeworkToken = homeworkResult.homework_token;
        console.log('✅ New homework created:', homeworkToken);
      }

      // 2. Create homework-student relationships for selected students
      const relationshipData = students
        .filter((student) => selectedStudents.has(student.student_token))
        .map((student) => ({
          homework_token: homeworkToken,
          student_token: student.student_token,
          class_token: classId,
          semester_year: year,
          article_token: selectedContent.id,
          created_at: new Date().toISOString(),
          completed: false,
          full_name: `${student.first_name} ${student.last_name}`,
        }));

      console.log('👥 Creating student assignments:', relationshipData.length);

      const { error: relationshipError } = await supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .insert(relationshipData);

      if (relationshipError) {
        // Check if it's a duplicate key error (23505)
        if (relationshipError.code === '23505') {
          console.log(
            'ℹ️ Duplicate assignment detected - some students already have this homework',
          );
          // Don't throw error for duplicates, just log and continue
        } else {
          console.error(
            'Error creating student relationships:',
            relationshipError,
          );
          throw relationshipError;
        }
      }

      console.log('✅ Homework assigned to students successfully');
      toast.close(loadingToastId);
      toast({
        title: 'Published Successfully!',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      const selectedStudentsList = students.filter((student) =>
        selectedStudents.has(student.student_token),
      );
      // Send notification email
      sendNotificationEmails(selectedContent, selectedStudentsList);
    } catch (contentError) {
      toast.close(loadingToastId);
      console.error(
        `Error publishing content ${selectedContent.title}:`,
        contentError,
      );
      toast({
        title: 'Publishing Failed',
        description: `Could not publish assignment ${contentError}.`,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setPublishing(false);
    }
    // Reset state
    onPublishModalClose();
  }, [
    supabase,
    classInfo,
    selectedContent,
    selectedHomework,
    selectedStudents,
    classId,
    user_token,
    full_name,
    students,
    toast,
    onPublishModalClose,
  ]);

  // Handle preview
  const handlePreview = useCallback(
    (contentId: string) => {
      navigate(`/teacher/content/${contentId}/view`);
    },
    [navigate],
  );

  // Handle selection
  const selectedTokens = useMemo(
    () => Array.from(selectedStudents),
    [selectedStudents],
  );

  const allStudentTokens = useMemo(
    () => students.map((student) => student.student_token),
    [students],
  );

  const handleStudentSelectionChange = useCallback((tokens: string[]) => {
    setSelectedStudents(new Set(tokens.map(String)));
  }, []);

  const handleSelectAllChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedStudents(new Set(allStudentTokens));
      } else {
        setSelectedStudents(new Set());
      }
    },
    [allStudentTokens],
  );

  const isAllSelected =
    allStudentTokens.length > 0 &&
    selectedTokens.length === allStudentTokens.length;

  const isIndeterminate =
    selectedTokens.length > 0 &&
    selectedTokens.length < allStudentTokens.length;

  // Handle publish precheck
  const handlePublishPrecheck = useCallback(
    async (content: ContentWithMetadata) => {
      if (!supabase || !classInfo || !content) {
        console.error('Missing required data for quick publish');
        return;
      }
      console.log('🚀 Precheck homework:', content.title);

      const loadingToastId = toast({
        title: 'Homework precheck',
        description: `Checking "${content.title}".`,
        status: 'loading',
        duration: null,
        isClosable: false,
      });

      // Set content
      setSelectedContent(content);

      // Reset student select list
      setSelectedStudents(new Set());
      setUnavailableStudents(new Set());

      // Set default student list
      try {
        // 1. check homework record
        const { data: existingHomework } = await supabase
          .from(TABLE_NAMES.HOMEWORK)
          .select('homework_token')
          .eq('article_token', content.id)
          .eq('class_token', classId)
          .eq('deleted', false);

        if (existingHomework && existingHomework.length > 0) {
          let homeworkToken = existingHomework[0].homework_token;
          setSelectedHomework(homeworkToken);
          console.log('📝 Found existing homework:', homeworkToken);

          // 2. check students that already assigned
          const { data: existingRelations } = await supabase
            .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
            .select('student_token')
            .eq('homework_token', homeworkToken)
            .eq('class_token', classId)
            .eq('article_token', content.id);

          const existingStudentTokens = new Set(
            existingRelations?.map((relation: any) => relation.student_token) ||
              [],
          );

          setUnavailableStudents(existingStudentTokens);

          // Homework is already assigned to all students
          if (existingStudentTokens.size === students.length) {
            // Close loading toast
            toast.close(loadingToastId);

            // Show info toast
            toast({
              title: 'Already Assigned',
              description: `"${content.title}" is already assigned to all students in this class.`,
              status: 'info',
              duration: 4000,
              isClosable: true,
            });
            return;
          } else {
            // Filter out students who already have this homework
            const studentsNeedingHomework = students
              .filter(
                (student) => !existingStudentTokens.has(student.student_token),
              )
              .map((student) => student.student_token);
            if (studentsNeedingHomework.length > 0) {
              setSelectedStudents(new Set(studentsNeedingHomework));
              console.log(
                `${studentsNeedingHomework.length} can be assigned with this homework`,
              );
            }
            toast.close(loadingToastId);

            // Open publish modal
            onPublishModalOpen();
          }
        } else {
          toast.close(loadingToastId);
          // All students can be assigned
          const studentsNeedingHomework = students.map(
            (student) => student.student_token,
          );
          if (studentsNeedingHomework.length > 0) {
            setSelectedStudents(new Set(studentsNeedingHomework));
            console.log(
              `${studentsNeedingHomework.length} can be assigned with this homework`,
            );
          }
          // Open publish modal
          onPublishModalOpen();
        }
      } catch (error) {
        console.error('Error in publish precheck:', error);
        toast.close(loadingToastId);
        toast({
          title: 'Publishing Failed',
          description: `Could not publish "${content.title}". Please check your connection and try again.`,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }
    },
    [supabase, students, classId, toast],
  );

  // Table columns
  const tableColumns: TableColumn<ContentWithMetadata>[] = [
    {
      key: 'title',
      header: 'Content',
      render: (content) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="bold" noOfLines={1}>
            {content.title}
          </Text>
          <Text fontSize="sm" color="gray.600" noOfLines={1}>
            {content.author}
          </Text>
          <HStack spacing={2}>
            <Badge size="sm" colorScheme="blue">
              Grade {content.grade}
            </Badge>
            {content.chapter && (
              <Badge size="sm" colorScheme="purple">
                Ch. {content.chapter}
              </Badge>
            )}
          </HStack>
        </VStack>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (content) => (
        <HStack spacing={1}>
          <Tooltip label="Preview">
            <IconButton
              aria-label="Preview content"
              icon={<FiEye />}
              size="sm"
              variant="ghost"
              onClick={() => handlePreview(content.id)}
            />
          </Tooltip>
          <Tooltip label="Publish">
            <IconButton
              aria-label="Publish"
              icon={<FiSend />}
              size="sm"
              variant="ghost"
              colorScheme="green"
              onClick={() => handlePublishPrecheck(content)}
            />
          </Tooltip>
        </HStack>
      ),
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <VStack spacing={6} align="stretch">
          <Text>Loading class information...</Text>
        </VStack>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              Publish Homework
            </Text>
            <Text color="gray.600">
              {classInfo?.class_name} • {classInfo?.grade} Grade
            </Text>
          </VStack>
          <HStack spacing={3}>
            <HStack spacing={1}>
              <Tooltip label="Grid View">
                <IconButton
                  aria-label="Grid view"
                  icon={<FiGrid />}
                  variant="ghost"
                  size="sm"
                  color={viewMode === 'grid' ? 'blue.500' : 'gray.400'}
                  onClick={() => setViewMode('grid')}
                />
              </Tooltip>
              <Tooltip label="Table View">
                <IconButton
                  aria-label="Table view"
                  icon={<FiList />}
                  variant="ghost"
                  size="sm"
                  color={viewMode === 'table' ? 'blue.500' : 'gray.400'}
                  onClick={() => setViewMode('table')}
                />
              </Tooltip>
            </HStack>
          </HStack>
        </HStack>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <StatsCard
            title="Available Content"
            value={stats.totalContent.toString()}
            subtitle="ready to publish"
            icon={FiBookOpen}
          />
          <StatsCard
            title="Target Students"
            value={stats.totalStudents.toString()}
            subtitle="in class"
            icon={FiUsers}
          />
        </SimpleGrid>

        {/* Filters */}
        <Card>
          <VStack spacing={4}>
            <VStack spacing={4} w="full">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <SelectInput
                  // label="Filter by Grade"
                  placeholder="Select grade"
                  value={filterBy}
                  onChange={(value: string) => setFilterBy(value as FilterBy)}
                  options={[
                    { value: 'all', label: 'All Grades' },
                    { value: 'grade4', label: 'Grade 4' },
                    { value: 'grade5', label: 'Grade 5' },
                    { value: 'grade6', label: 'Grade 6' },
                    { value: 'grade7', label: 'Grade 7' },
                    { value: 'grade8', label: 'Grade 8' },
                  ]}
                />
              </SimpleGrid>
            </VStack>
          </VStack>
        </Card>

        {/* Pagination Controls */}
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              Show:
            </Text>
            <Select
              size="sm"
              w="80px"
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurrentPage(1); // Reset to first page
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
            <Text fontSize="sm" color="gray.600">
              items per page
            </Text>
          </HStack>

          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              {filteredTotalCount > 0 ? (
                <>
                  Showing {(currentPage - 1) * pageSize + 1}-
                  {Math.min(currentPage * pageSize, filteredTotalCount)} of{' '}
                  {filteredTotalCount} articles
                </>
              ) : (
                'No articles found'
              )}
            </Text>
          </HStack>

          <HStack spacing={1}>
            <IconButton
              aria-label="Previous page"
              icon={<FiChevronLeft />}
              size="sm"
              variant="ghost"
              isDisabled={currentPage === 1 || filteredTotalCount === 0}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            />

            {/* Page numbers */}
            {(() => {
              const pages = [];
              const showPages = 5; // Show 5 page numbers
              const startPage = Math.max(
                1,
                currentPage - Math.floor(showPages / 2),
              );
              const endPage = Math.min(totalPages, startPage + showPages - 1);

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <Button
                    key={i}
                    size="sm"
                    variant={
                      i === currentPage ? ('solid' as any) : ('ghost' as any)
                    }
                    colorScheme={i === currentPage ? 'blue' : 'gray'}
                    onClick={() => setCurrentPage(i)}
                    minW="32px"
                  >
                    {i}
                  </Button>,
                );
              }

              return pages;
            })()}

            <IconButton
              aria-label="Next page"
              icon={<FiChevronRight />}
              size="sm"
              variant="ghost"
              isDisabled={filteredTotalCount === 0 || currentPage >= totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            />
          </HStack>
        </HStack>

        {/* Content List */}
        <Tabs variant="line" colorScheme="blue">
          <TabList>
            <Tab>All Content ({filteredTotalCount})</Tab>
            <Tab>Recently Published</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              {libraryLoading ? (
                <Card>
                  <VStack spacing={4} py={8}>
                    <Text color="gray.500">Loading articles...</Text>
                    <Progress
                      size="xs"
                      isIndeterminate
                      colorScheme="blue"
                      w="200px"
                    />
                  </VStack>
                </Card>
              ) : viewMode === 'table' ? (
                <DataTable data={paginatedContent} columns={tableColumns} />
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                  {paginatedContent.map((content) => (
                    <Card
                      key={content.id}
                      _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
                      position="relative"
                      display="flex"
                      flexDirection="column"
                      height="100%"
                    >
                      <VStack spacing={4} align="stretch" flex="1">
                        {/* Selection Checkbox */}
                        <HStack justify="space-between">
                          <HStack spacing={2}>
                            <Badge colorScheme="blue" size="sm">
                              Grade {content.grade}
                            </Badge>
                            {content.chapter && (
                              <Badge colorScheme="purple" size="sm">
                                Ch. {content.chapter}
                              </Badge>
                            )}
                          </HStack>
                        </HStack>

                        {/* Content Info */}
                        <VStack spacing={2} align="start">
                          <Text fontWeight="bold" fontSize="lg" noOfLines={2}>
                            {content.title}
                          </Text>
                          <Text fontSize="sm" color="gray.600" noOfLines={1}>
                            by {content.author}
                          </Text>
                        </VStack>

                        {/* Stats */}
                        {/* <VStack spacing={2} align="start" fontSize="sm">
                          <HStack>
                            <FiClock />
                            <Text color="gray.600">Estimated Time:</Text>
                            <Text fontWeight="bold">
                              {content.estimatedTime} min
                            </Text>
                          </HStack>
                        </VStack> */}

                        {/* Spacer to push buttons to bottom */}
                        <Box flex="1" />

                        {/* Action Buttons */}
                        <VStack spacing={2}>
                          <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<FiEye />}
                            onClick={() => handlePreview(content.id)}
                            width="full"
                          >
                            Preview
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="green"
                            leftIcon={<FiSend />}
                            onClick={() => handlePublishPrecheck(content)}
                            width="full"
                          >
                            Publish
                          </Button>
                        </VStack>
                      </VStack>
                    </Card>
                  ))}
                </SimpleGrid>
              )}

              {/* Bottom Pagination */}
              {!libraryLoading && filteredTotalCount > pageSize && (
                <HStack justify="center" spacing={1} mt={6}>
                  <IconButton
                    aria-label="Previous page"
                    icon={<FiChevronLeft />}
                    size="sm"
                    variant="ghost"
                    isDisabled={currentPage === 1 || filteredTotalCount === 0}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  />

                  {/* Page numbers */}
                  {(() => {
                    const pages = [];
                    const showPages = 5; // Show 5 page numbers
                    const startPage = Math.max(
                      1,
                      currentPage - Math.floor(showPages / 2),
                    );
                    const endPage = Math.min(
                      totalPages,
                      startPage + showPages - 1,
                    );

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          size="sm"
                          variant={
                            i === currentPage
                              ? ('solid' as any)
                              : ('ghost' as any)
                          }
                          colorScheme={i === currentPage ? 'blue' : 'gray'}
                          onClick={() => setCurrentPage(i)}
                          minW="32px"
                        >
                          {i}
                        </Button>,
                      );
                    }

                    return pages;
                  })()}

                  <IconButton
                    aria-label="Next page"
                    icon={<FiChevronRight />}
                    size="sm"
                    variant="ghost"
                    isDisabled={
                      filteredTotalCount === 0 || currentPage >= totalPages
                    }
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  />
                </HStack>
              )}
            </TabPanel>

            <TabPanel px={0}>
              <Card>
                <VStack spacing={4} py={8}>
                  <Text color="gray.500">
                    Recently published content will appear here
                  </Text>
                </VStack>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Publish Modal */}
        <Modal
          isOpen={isPublishModalOpen}
          onClose={onPublishModalClose}
          size="3xl"
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              Publish Assignment: {selectedContent?.title}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={6} align="stretch">
                {/* Student Selection */}
                <FormControl>
                  <FormLabel>
                    Target Students ({selectedStudents.size}/{students.length})
                  </FormLabel>
                  <Box
                    maxH="200px"
                    overflowY="auto"
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={4}
                  >
                    <CheckboxGroup
                      value={selectedTokens}
                      onChange={handleStudentSelectionChange}
                    >
                      <VStack spacing={2} align="start">
                        <Checkbox
                          isChecked={isAllSelected}
                          isIndeterminate={isIndeterminate}
                          onChange={handleSelectAllChange}
                          isDisabled={students.length === 0}
                        >
                          <Text fontWeight="bold">Select All</Text>
                        </Checkbox>
                        <Divider />
                        {students.map((student) => {
                          const token = student.student_token;
                          const isPreselected = selectedStudents.has(token);
                          const isUnavailable = unavaiableStudents.has(token);

                          return (
                            <Checkbox
                              key={token}
                              value={token}
                              isDisabled={isUnavailable}
                              colorScheme={isUnavailable ? 'gray' : 'green'}
                            >
                              <HStack spacing={2}>
                                <Avatar
                                  size="xs"
                                  name={`${student.first_name} ${student.last_name}`}
                                />
                                <Text>
                                  {student.first_name} {student.last_name}
                                </Text>
                              </HStack>
                            </Checkbox>
                          );
                        })}
                      </VStack>
                    </CheckboxGroup>
                  </Box>
                </FormControl>

                {/* Content Information */}
                {/* <Box bg="gray.50" p={4} borderRadius="md">
                  <VStack spacing={2} align="start">
                    <Text fontSize="sm" color="gray.600">
                      Content Details
                    </Text>
                    <HStack spacing={4}>
                      <Badge colorScheme="blue">
                        Grade {selectedContent?.grade}
                      </Badge>
                      <Badge
                        colorScheme={
                          selectedContent?.difficulty === 'easy'
                            ? 'green'
                            : selectedContent?.difficulty === 'medium'
                            ? 'yellow'
                            : 'red'
                        }
                      >
                        {selectedContent?.difficulty?.toUpperCase()}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm">
                      Estimated time: {selectedContent?.estimatedTime} minutes
                    </Text>
                  </VStack>
                </Box> */}

                {/* Preview */}
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    This assignment will be published to {selectedStudents.size}{' '}
                    students.
                  </AlertDescription>
                </Alert>

                {/* Action Buttons */}
                <HStack justify="flex-end" spacing={3}>
                  <Button variant="secondary" onClick={onPublishModalClose}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="blue"
                    leftIcon={<FiSend />}
                    onClick={executePublish}
                    isLoading={publishing}
                    loadingText="Publishing..."
                    isDisabled={selectedStudents.size === 0}
                  >
                    Publish
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </AdminLayout>
  );
};
